/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import getConnection from '@ion/core/src/network/ConnectionStatus';
import {NetworkStatus, Observer} from '@ion/core/src/network/types';

/**
 * The `useNetworkStatus` hook allows for observing the current network state
 * of the application, returning `true` when the application is online, and
 * `false` when the application is offline.
 *
 * ```typescript
 *   import {useNetworkStatus} from '@ion/applications/src/hooks/';
 *
 *   const isOnline: boolean = useNetworkStatus();
 *
 *   useEffect(() => {
 *     if (!isOnline) {
 *       // implement offline fallback
 *     }
 *   }, [isOnline]);
 * ```
 *
 * In addition to observing the application's network state, clients can pass
 * optional `onOffline` and `onOnline` callbacks which will be invoked when a
 * network  status change is detected. For convenience, a single callback can
 * also be used for both events by setting the `observeAll` argument to false,
 * in which case the `onOffline` callback will be used for both events.
 *
 * ```typescript
 *   import {useNetworkStatus} from '@ion/applications/src/hooks/';
 *
 *   // simply observe the online / offline state ...
 *   const isOnline = useNetworkStatus();
 *
 *    // individual callbacks invoked upon online and offline state changes ...
 *   useNetworkStatus(onOffline, onOnline);
 *
 *   // or single callback for both online and offline state changes ...
 *   useNetworkStatus(onNetworkStatusChange, null, true);
 * ```
 *
 * @see getConnection
 * @see NetworkStatus
 * @see Observer
 *
 * @param   onOffline An optional `Observer` which is to be invoked when the application
 *          goes offline.
 * @param   onOnline An optional `Observer` which is to be invoked when the application
 *          goes online.
 * @param   observeAll When true, a single observer will be used for both the
 *          offline and online states. When this argument is set to true, the
 *          first argument (`onOffline`) observer will be used for both
 *          network connection states.
 * @returns true if the application is currently online, otherwise false if the
 *          application is offline.
 */
export const useNetworkStatus = (onOffline?: Observer, onOnline?: Observer, observeAll = false): boolean => {
  const [isOnline, setIsOnline] = useState<boolean>();

  const observer = useCallback((isConnected: boolean) => {
    setIsOnline(isConnected);
    if (!observeAll) {
      !isConnected && onOffline?.(isConnected);
      isConnected && onOnline?.(isConnected);
    } else {
      onOffline?.(isConnected);
    }
  }, []);

  useEffect(() => {
    const network: NetworkStatus = getConnection();
    network.addObservers(observer, null, true);
    setIsOnline(network.isConnected);
    return () => {
      network.removeOfflineObserver(observer);
      network.removeOnlineObserver(observer);
    };
  }, []);

  return isOnline;
};
export default useNetworkStatus;
